import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Stethoscope, ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const DEMO = [
  ["Principal", "principal@elysiomdental.co.za"],
  ["Practitioner", "dentist@elysiomdental.co.za"],
  ["Receptionist", "reception@elysiomdental.co.za"],
  ["Billing", "billing@elysiomdental.co.za"],
];

export default function Login() {
  const navigate = useNavigate();
  const { finishLogin } = useAuth();
  const [email, setEmail] = useState("principal@elysiomdental.co.za");
  const [password, setPassword] = useState("Demo@2026");
  const [loading, setLoading] = useState(false);
  const [totpStep, setTotpStep] = useState(null); // temp_token
  const [code, setCode] = useState("");

  const routeByRole = (u) => navigate(u.role === "owner" ? "/owner" : "/app/dashboard");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email: email.trim().toLowerCase(), password });
      if (data.requires_totp) {
        setTotpStep(data.temp_token);
        toast.info("Owner account — enter your 6-digit authenticator code");
      } else {
        finishLogin(data.token, data.user);
        routeByRole(data.user);
      }
    } catch (e2) {
      toast.error(apiErr(e2));
    } finally {
      setLoading(false);
    }
  };

  const submitTotp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-totp", { temp_token: totpStep, code: code.trim() });
      finishLogin(data.token, data.user);
      routeByRole(data.user);
    } catch (e2) {
      toast.error(apiErr(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-background bg-radial-fade px-5">
      <div className="absolute inset-0 bg-grid opacity-30" />
      <Card className="relative w-full max-w-md rounded-2xl border-border/60 p-8 shadow-xl animate-rise">
        <button
          onClick={() => navigate("/")}
          data-testid="back-home-btn"
          className="mb-6 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to home
        </button>
        <div className="mb-6 flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <Stethoscope className="h-5 w-5" />
          </div>
          <div>
            <div className="font-bold tracking-tight">Practice OS™</div>
            <div className="text-xs text-muted-foreground">Sign in to your workspace</div>
          </div>
        </div>

        {!totpStep ? (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="login-email" value={email}
                onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="login-password" value={password}
                onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
            </div>
            <Button type="submit" data-testid="login-submit" className="w-full rounded-full" disabled={loading}>
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        ) : (
          <form onSubmit={submitTotp} className="space-y-4">
            <div className="rounded-xl bg-secondary p-3 text-xs text-muted-foreground">
              <ShieldCheck className="mb-1 inline h-4 w-4 text-primary" /> Two-factor authentication is required for the Owner account.
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="totp">6-digit code</Label>
              <Input id="totp" inputMode="numeric" maxLength={6} data-testid="totp-code" value={code}
                onChange={(e) => setCode(e.target.value)} placeholder="000000" required />
            </div>
            <Button type="submit" data-testid="totp-submit" className="w-full rounded-full" disabled={loading}>
              {loading ? "Verifying…" : "Verify & continue"}
            </Button>
          </form>
        )}

        <div className="mt-6 border-t border-border/60 pt-4">
          <div className="label-eyebrow mb-2">Demo accounts · password Demo@2026</div>
          <div className="grid grid-cols-2 gap-2">
            {DEMO.map(([role, mail]) => (
              <button
                key={mail}
                type="button"
                data-testid={`demo-${role.toLowerCase()}`}
                onClick={() => { setEmail(mail); setPassword("Demo@2026"); }}
                className="rounded-lg border border-border/60 px-2 py-1.5 text-left text-[11px] hover:border-primary/40 hover:bg-secondary"
              >
                <div className="font-semibold">{role}</div>
                <div className="truncate text-muted-foreground">{mail}</div>
              </button>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}
